import { createElement, Fragment, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { sanitizeInlineHtml } from '../../lib/sanitize'
import { highlightExcerpts } from '../../lib/takes'

// Authored inline HTML is rendered through the DOM, not injected: sanitise,
// parse into a detached fragment, mark the viewer's taken lines by splitting
// text nodes, then turn the (allowlisted) nodes into React elements. React
// owns every node on the page, so highlights and unfold toggles re-render
// cleanly instead of fighting innerHTML.

/** Live unfold anchors (`<span data-unfold>`) inside a block. */
export interface UnfoldControls {
  /** An unfold block hangs from this anchor (otherwise the words render plain). */
  has(anchor: string): boolean
  isOpen(anchor: string): boolean
  toggle(anchor: string): void
  /** id of the card element, for aria-controls. */
  cardId(anchor: string): string
}

/** Margin notes made from selected words (`<mark data-note>`): the words show their note. */
export interface NoteControls {
  /** A margin note hangs from this anchor (otherwise the words are a plain highlight). */
  has(anchor: string): boolean
  /** Bring the note into view and light it. */
  show(anchor: string): void
  /** id of the note's element, for aria-controls. */
  noteId(anchor: string): string
}

const FORMATTING = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code'])

function selectionInside(el: Element): boolean {
  const sel = window.getSelection()
  return !!sel && !sel.isCollapsed && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer)
}

function convert(parent: Node, unfold: UnfoldControls | undefined, notes?: NoteControls): ReactNode[] {
  const out: ReactNode[] = []
  parent.childNodes.forEach((node, i) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push((node as Text).data)
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as Element
    const tag = el.tagName.toLowerCase()
    const kids = convert(el, unfold, notes)
    if (tag === 'br') {
      out.push(<br key={i} />)
    } else if (tag === 'mark' && el.hasAttribute('data-take-mark')) {
      out.push(
        <mark key={i} data-take-mark="">
          {kids}
        </mark>,
      )
    } else if (tag === 'mark' && el.hasAttribute('data-note') && notes?.has(el.getAttribute('data-note')!)) {
      // Words a margin note is about: underlined as in the editor; clicking them shows the note.
      const anchor = el.getAttribute('data-note')!
      const show = (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
        if (e.type === 'click' && selectionInside(e.currentTarget)) return
        notes.show(anchor)
      }
      out.push(
        <mark
          key={i}
          data-note={anchor}
          role="button"
          tabIndex={0}
          title="Show the margin note"
          aria-controls={notes.noteId(anchor)}
          onClick={show}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              show(e)
            }
          }}
        >
          {kids}
        </mark>,
      )
    } else if (tag === 'mark') {
      // The author's highlight (sanitised, so it has no attributes; a reader's taken line always does).
      out.push(<mark key={i}>{kids}</mark>)
    } else if (tag === 'a') {
      const href = el.getAttribute('href')
      out.push(
        href ? (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer nofollow">
            {kids}
          </a>
        ) : (
          <Fragment key={i}>{kids}</Fragment>
        ),
      )
    } else if (tag === 'span' && el.hasAttribute('data-unfold')) {
      const anchor = el.getAttribute('data-unfold')!
      if (!unfold?.has(anchor)) {
        out.push(<Fragment key={i}>{kids}</Fragment>)
        return
      }
      const open = unfold.isOpen(anchor)
      const toggle = (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
        // Dragging across the words to select them shouldn't also unfold.
        if (e.type === 'click' && selectionInside(e.currentTarget)) return
        unfold.toggle(anchor)
      }
      out.push(
        <span
          key={i}
          role="button"
          tabIndex={0}
          data-unfold={anchor}
          aria-expanded={open}
          aria-controls={open ? unfold.cardId(anchor) : undefined}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggle(e)
            }
          }}
        >
          {kids}
        </span>,
      )
    } else if (FORMATTING.has(tag)) {
      out.push(createElement(tag, { key: i }, ...kids))
    } else {
      out.push(<Fragment key={i}>{kids}</Fragment>)
    }
  })
  return out
}

/** Stored inline HTML as React nodes, with `marks` highlighted. */
export function inlineHtmlNodes(html: string, marks: readonly string[] = [], unfold?: UnfoldControls, notes?: NoteControls): ReactNode[] {
  const clean = sanitizeInlineHtml(html)
  if (!clean) return []
  const tpl = document.createElement('template')
  tpl.innerHTML = clean
  highlightExcerpts(tpl.content, marks)
  return convert(tpl.content, unfold, notes)
}

/** Plain text (a thought's snapshot) as React nodes, with `marks` highlighted. */
export function plainTextNodes(text: string, marks: readonly string[] = []): ReactNode[] {
  if (marks.length === 0) return [text]
  const holder = document.createElement('div')
  holder.textContent = text
  highlightExcerpts(holder, marks)
  return convert(holder, undefined)
}
